import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools.js";
import type { Transaction } from "@/utils/types.js";
import type { Account } from "@aircap/common/account";
import type { SwanTransaction } from "@aircap/common/swan";
import { getSwanTransactionAmount } from "@aircap/common/swan";
import { collection, doc, orderBy, query, updateDoc, where } from "firebase/firestore";
import { computed, type Ref } from "vue";
import type { VueFirestoreDocumentData } from "vuefire";

export const useSwanTransactions = (
  account: Ref<VueFirestoreDocumentData<Account> | undefined>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>;
    isFuture?: Ref<boolean>;
  },
) => {
  const { t: $t, locale } = useI18n();

  const transactionsSource = computed(() => {
    if (account.value?.accountType !== "swan") return null;
    return query(
      collection<SwanTransaction>(db, `accounts/${account.value?.id}/swanTransactions`),
      ...(filters?.beneficiaryIban?.value
        ? [where<SwanTransaction>("counterpartyIban", "==", filters.beneficiaryIban.value)]
        : []),
      ...(filters?.isFuture?.value === false
        ? [where<SwanTransaction>("status", "==", "Booked")]
        : []),
      ...(filters?.isFuture?.value === true
        ? [where<SwanTransaction>("status", "in", ["Pending", "Upcoming"])]
        : []),
      ...(filters?.isFuture?.value === undefined
        ? [where<SwanTransaction>("status", "in", ["Booked", "Pending", "Upcoming"])]
        : []),
      orderBy<SwanTransaction>("executionDate", "desc"),
    );
  });

  const { data: transactionCollection, pending: loading } =
    useCollection<SwanTransaction>(transactionsSource);

  const transactions = computed<Transaction[]>(() => {
    const swanAccountData = account.value?.swan_account;
    if (!swanAccountData) return [];

    return transactionCollection.value.map((t) => {
      return {
        id: t.id,
        date: t.executionDate,
        name: t.label,
        amount: getSwanTransactionAmount(t),
        currencyCode: t.amount.currency,
        counterparty: t.counterparty,
        counterpartyIban: t.counterpartyIban,
        category: t.categoryId
          ? { id: t.categoryId, name: $t(`categories.${t.categoryId}`) }
          : undefined,
        trackedCategorySplits: t.trackedCategorySplits,
        suggestedTrackedCategoryId: t.suggestedTrackedCategoryId,
        rejectedSuggestedTrackedCategoryId: t.rejectedSuggestedTrackedCategoryId,
        amountFormatted: formatCurrency({
          value: getSwanTransactionAmount(t),
          currencyCode: t.amount.currency,
          sign: true,
          locale: locale.value,
        }),
        is_future: t.status === "Pending" || t.status === "Upcoming",
        dateFormatted: {
          short: new Intl.DateTimeFormat(locale.value).format(new Date(t.executionDate)),
          long: new Intl.DateTimeFormat(locale.value, {
            dateStyle: "long",
            timeStyle: "short",
          }).format(new Date(t.executionDate)),
        },
        is_flagged: t.is_flagged,
        label: t.label,
        reference: t.reference,
        transferRequest: t.transferRequest
          ? {
              ...t.transferRequest,
              transactionId: t.transferRequest.swan_transaction_id,
              files: t.transferRequest.files,
              label: t.transferRequest.label,
              reference: t.transferRequest.reference,
              trackedCategoryId: t.transferRequest.trackedCategoryId,
              decisions: t.transferRequest.decisions,
            }
          : undefined,
        files: [
          ...(t.files?.map((f) => ({
            ...f,
            source: "transaction" as const,
          })) ?? []),
          ...(t.transferRequest?.files?.map((f) => ({
            ...f,
            source: "transferRequest" as const,
          })) ?? []),
        ],
      } satisfies Transaction;
    });
  });

  const flagTransaction = (transactionId: string, value: boolean) => {
    if (!account.value?.id) return;
    const transactionRef = doc<SwanTransaction>(
      db,
      `accounts/${account.value.id}/swanTransactions`,
      transactionId,
    );
    void updateDoc(transactionRef, { is_flagged: value });
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
